import {
  DEFAULT_CONVERSION_CURRENCY,
  cleanConversionCurrency,
  cleanConversionText,
  isLocalConversionHostname,
  normalizeConversionCountryCode,
  normalizeConversionEmail,
  normalizeConversionPhone,
  normalizeDollars,
  omitEmptyConversionFields,
} from "./adConversionTracking";
import { hasBrowserAdvertisingPrivacyOptOut } from "./paidConversionPublishing";

const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || "";
const GOOGLE_PURCHASE_LABEL =
  process.env.REACT_APP_GOOGLE_ADS_PURCHASE_CONVERSION_LABEL || "";
const GOOGLE_ONBOARDING_SERVICEABLE_LABEL =
  process.env.REACT_APP_GOOGLE_ADS_ONBOARDING_SERVICEABLE_CONVERSION_LABEL ||
  "";
const GOOGLE_ADS_CONFIGURED_IDS_KEY =
  "__LINKED_LIVES_GOOGLE_ADS_CONFIGURED_IDS__";

export function getGoogleAdsConversionConfig() {
  return {
    conversionId: cleanConversionText(GOOGLE_ADS_ID),
    purchaseConversionLabel: cleanConversionText(GOOGLE_PURCHASE_LABEL),
    onboardingServiceableConversionLabel: cleanConversionText(
      GOOGLE_ONBOARDING_SERVICEABLE_LABEL,
    ),
  };
}

function canUseDom() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isLocalhost() {
  if (!canUseDom()) return true;
  return isLocalConversionHostname(window.location?.hostname);
}

function canRunGoogleAdsTracking() {
  return (
    canUseDom() &&
    !isLocalhost() &&
    !hasBrowserAdvertisingPrivacyOptOut()
  );
}

function ensureDataLayer() {
  if (!canRunGoogleAdsTracking()) return null;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  return window.gtag;
}

function getConfiguredIds() {
  if (!canUseDom()) return {};
  if (
    !window[GOOGLE_ADS_CONFIGURED_IDS_KEY] ||
    typeof window[GOOGLE_ADS_CONFIGURED_IDS_KEY] !== "object"
  ) {
    window[GOOGLE_ADS_CONFIGURED_IDS_KEY] = {};
  }
  return window[GOOGLE_ADS_CONFIGURED_IDS_KEY];
}

export function configureGoogleAdsTag() {
  const { conversionId } = getGoogleAdsConversionConfig();
  if (!conversionId || !canRunGoogleAdsTracking()) return false;

  const gtag = ensureDataLayer();
  if (!gtag) return false;

  if (
    !document.querySelector(
      `script[data-linked-lives-google-ads="${conversionId}"]`,
    )
  ) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      conversionId,
    )}`;
    script.dataset.linkedLivesGoogleAds = conversionId;
    document.head.appendChild(script);
  }

  const configuredIds = getConfiguredIds();
  if (!configuredIds[conversionId]) {
    gtag("js", new Date());
    gtag("config", conversionId, { allow_enhanced_conversions: true });
    configuredIds[conversionId] = true;
  }

  return true;
}

function resolveGoogleAdsEnhancedConversionAddress({
  address = {},
  firstName = "",
  lastName = "",
  street = "",
  city = "",
  region = "",
  postalCode = "",
  country = "",
} = {}) {
  const addressData = omitEmptyConversionFields({
    first_name: cleanConversionText(firstName || address.firstName),
    last_name: cleanConversionText(lastName || address.lastName),
    street: cleanConversionText(
      street || address.line1 || address.street || address.streetAddress,
    ),
    city: cleanConversionText(city || address.city || address.locality),
    region: cleanConversionText(
      region ||
        address.stateCode ||
        address.state_code ||
        address.region_code ||
        address.state ||
        address.region,
    ),
    postal_code: cleanConversionText(
      postalCode ||
        address.postalCode ||
        address.zipCode ||
        address.postal ||
        address.zip,
    ),
    country: normalizeConversionCountryCode(
      country ||
        address.countryCode ||
        address.country_code ||
        address.country ||
        "US",
    ),
  });

  if (
    !addressData.first_name ||
    !addressData.last_name ||
    !addressData.postal_code ||
    !addressData.country
  ) {
    return null;
  }

  return addressData;
}

export function buildGoogleAdsEnhancedConversionUserData({
  email = "",
  phone = "",
  firstName = "",
  lastName = "",
  address = {},
  street = "",
  city = "",
  region = "",
  postalCode = "",
  country = "",
} = {}) {
  const addressData = resolveGoogleAdsEnhancedConversionAddress({
    address,
    firstName,
    lastName,
    street,
    city,
    region,
    postalCode,
    country,
  });
  const normalizedEmail = normalizeConversionEmail(email);
  const normalizedPhone = normalizeConversionPhone(phone);
  const userData = omitEmptyConversionFields({
    email: normalizedEmail,
    ...(addressData ? { address: addressData } : {}),
  });

  if (normalizedPhone && (userData.email || userData.address)) {
    userData.phone_number = normalizedPhone;
  }

  return Object.keys(userData).length ? userData : null;
}

export function buildGoogleAdsPurchaseConversionPayload({
  conversionId,
  purchaseConversionLabel,
  transactionId,
  value,
  currency = DEFAULT_CONVERSION_CURRENCY,
  newCustomer = null,
} = {}) {
  const cleanConversionId = cleanConversionText(conversionId);
  const cleanPurchaseConversionLabel = cleanConversionText(
    purchaseConversionLabel,
  );
  if (!cleanConversionId || !cleanPurchaseConversionLabel) return null;

  const numericValue = Number(value);
  const normalizedValue = Number.isFinite(numericValue)
    ? normalizeDollars(numericValue)
    : 1;

  return omitEmptyConversionFields({
    send_to: `${cleanConversionId}/${cleanPurchaseConversionLabel}`,
    transaction_id: cleanConversionText(transactionId),
    value: normalizedValue,
    currency: cleanConversionCurrency(currency),
    new_customer: typeof newCustomer === "boolean" ? newCustomer : undefined,
  });
}

export function buildGoogleAdsOnboardingServiceableConversionPayload({
  conversionId,
  onboardingServiceableConversionLabel,
  transactionId,
} = {}) {
  const cleanConversionId = cleanConversionText(conversionId);
  const cleanLabel = cleanConversionText(
    onboardingServiceableConversionLabel,
  );
  if (!cleanConversionId || !cleanLabel) return null;

  return omitEmptyConversionFields({
    send_to: `${cleanConversionId}/${cleanLabel}`,
    transaction_id: cleanConversionText(transactionId),
  });
}

function trackGoogleAdsConversion({
  conversionPayload,
  email = "",
  phone = "",
  firstName = "",
  lastName = "",
  address = {},
  street = "",
  city = "",
  region = "",
  postalCode = "",
  country = "",
} = {}) {
  if (!conversionPayload || !canRunGoogleAdsTracking()) return false;
  if (!configureGoogleAdsTag()) return false;

  const gtag = ensureDataLayer();
  if (!gtag) return false;

  const userData = buildGoogleAdsEnhancedConversionUserData({
    email,
    phone,
    firstName,
    lastName,
    address,
    street,
    city,
    region,
    postalCode,
    country,
  });
  if (userData) {
    gtag("set", "user_data", userData);
  }

  gtag("event", "conversion", conversionPayload);
  return true;
}

export function trackGoogleAdsPurchaseConversion({
  transactionId,
  value,
  currency = DEFAULT_CONVERSION_CURRENCY,
  email = "",
  phone = "",
  firstName = "",
  lastName = "",
  address = {},
  street = "",
  city = "",
  region = "",
  postalCode = "",
  country = "",
  newCustomer = null,
} = {}) {
  const { conversionId, purchaseConversionLabel } =
    getGoogleAdsConversionConfig();
  return trackGoogleAdsConversion({
    conversionPayload: buildGoogleAdsPurchaseConversionPayload({
      conversionId,
      purchaseConversionLabel,
      transactionId,
      value,
      currency,
      newCustomer,
    }),
    email,
    phone,
    firstName,
    lastName,
    address,
    street,
    city,
    region,
    postalCode,
    country,
  });
}

export function trackGoogleAdsOnboardingServiceableConversion({
  transactionId,
  email = "",
  phone = "",
  firstName = "",
  lastName = "",
  address = {},
  street = "",
  city = "",
  region = "",
  postalCode = "",
  country = "",
} = {}) {
  const { conversionId, onboardingServiceableConversionLabel } =
    getGoogleAdsConversionConfig();

  return trackGoogleAdsConversion({
    conversionPayload: buildGoogleAdsOnboardingServiceableConversionPayload({
      conversionId,
      onboardingServiceableConversionLabel,
      transactionId,
    }),
    email,
    phone,
    firstName,
    lastName,
    address,
    street,
    city,
    region,
    postalCode,
    country,
  });
}
